import clsx from 'clsx';

export type TagVariant =
  | 'default'
  | 'org'
  | 'account'
  | 'gray'
  | 'magenta'
  | 'cyan'
  | 'sea-green'
  | 'purple'
  | 'green'
  | 'orange'
  | 'spiro'
  | 'blue'
  | 'dark-blue'
  | 'pink'
  | 'cluster'
  | 'instance'
  | 'instance_tag'
  | 'volume'
  | 'node'
  | 'node-active'
  | 'aws-magenta'
  | 'aws-mint-green'
  | 'aws-gray-light'
  | 'aws-gray-dark'
  | 'aws-ocean-blue'
  | 'aws-deep-pink'
  | 'kpi';

type Size = 'xs' | 'sm' | 'md' | 'status' | 'kpi';

export default function Tag({
  variant = 'default',
  size = 'md',
  text,
  progress,
  onRemove,
  className,
  dataTooltipId,
  showFullText = false,
  truncateToContainer = false,
}: {
  variant?: TagVariant;
  size?: Size;
  text: string;
  progress?: number;
  onRemove?: () => void;
  className?: string;
  dataTooltipId?: string;
  showFullText?: boolean;
  truncateToContainer?: boolean;
}) {
  const transitionalStatus = !isNaN(Number(progress));
  const variantClasses = getVariantClasses(variant || 'default');
  const sizeClasses = getSizeClasses(size);
  const shouldTruncate = !showFullText || truncateToContainer;

  return (
    <div
      role="tag"
      aria-label={text}
      id={dataTooltipId}
      data-tooltip-id={dataTooltipId}
      className={clsx(
        'rounded  font-family-inter flex items-center',
        truncateToContainer && 'min-w-0 w-fit max-w-full',
        !showFullText && !truncateToContainer && 'w-fit max-w-40',
        sizeClasses,
        variantClasses,
        className,
        transitionalStatus ? 'animate-breathe' : '',
        showFullText && !truncateToContainer ? 'w-fit' : ''
      )}
    >
      <p className={clsx('whitespace-nowrap', shouldTruncate && 'min-w-0 overflow-hidden text-ellipsis')}>
        {text}
        {transitionalStatus && <span className="font-normal">{` (${progress}%)`}</span>}
      </p>
      {onRemove && (
        <button
          className="ml-1 text-xs font-bold  transition-colors cursor-pointer"
          onClick={onRemove}
          aria-label="Remove tag"
        >
          &times;
        </button>
      )}
    </div>
  );
}

function getSizeClasses(size: Size): string {
  switch (size) {
    case 'status':
      return 'px-1.5 py-1 h-6  text-xs font-[500] leading-5 tracking-normal';
    case 'xs':
      return 'px-1 py-1 h-4  text-[11px] font-[500] leading-4 tracking-normal';
    case 'sm':
      return 'px-1.5 py-1 h-7  text-xs font-[500] leading-5 tracking-normal';
    case 'kpi':
      return 'px-1.5 py-1 text-xs font-[700] leading-4 tracking-normal';
    case 'md':
    default:
      return 'px-2 py-1 h-7 text-sm font-[500] leading-5 tracking-normal';
  }
}

// Color Variant Helper Function (Unchanged)
function getVariantClasses(variant: TagVariant): string {
  switch (variant) {
    case 'gray':
      return 'bg-azureish-white text-dark-blue';
    case 'magenta':
      return 'bg-magenta text-white';
    case 'sea-green':
      return 'bg-sea-green text-white';
    case 'purple':
      return 'bg-electric-indigo text-white';
    case 'pink':
      return 'bg-pink text-white';
    case 'green':
      return 'bg-green text-white';
    case 'orange':
      return 'bg-orange text-white';
    case 'spiro':
      return 'bg-spiro text-white';
    case 'blue':
      return 'bg-primary-200 text-primary-600';
    case 'dark-blue':
      return 'bg-primary-100 text-dark-blue';
    case 'cluster':
      return 'bg-magnolia text-vivd-purple';
    case 'node':
      return 'bg-pale-yellow text-carrot-orange';
    case 'node-active':
      return 'text-pale-yellow bg-carrot-orange';
    case 'instance':
    case 'instance_tag':
      return 'bg-primary-100 text-primary-600';
    case 'volume':
      return 'bg-primary-50 text-gray-700';
    case 'aws-magenta':
      return 'bg-magnolia-accent text-dark-magenta';
    case 'aws-mint-green':
      return 'bg-light-mint-green text-mint-green';
    case 'aws-gray-light':
      return 'bg-gray-200 text-gray-600';
    case 'aws-gray-dark':
      return 'bg-gray-300 text-gray-600';
    case 'aws-ocean-blue':
      return 'bg-sky-blue text-dark-blue';
    case 'aws-deep-pink':
      return 'bg-pale-pink text-deep-pink';
    case 'kpi':
      return 'bg-bg text-gray-700 font-[700] text-sm';
    case 'org':
    case 'account':
    default:
      return 'bg-gray-200 text-primary-900';
  }
}
